/**
 * Store page component — Active route: App.jsx → /store → store.jsx → StoreManager.jsx
 *
 * Canonical backend contract: GraphQL via AppSync (GRAPHQL_URL in config.js).
 * GraphQL resolver: ss-api-store/src/v1/graphql/storeResolver.ts (on develop).
 *
 * When storeEnabled is false → renders onboarding prompt (calls /store/v1/connect/onboarding).
 * When storeEnabled is true  → renders StoreManager (GraphQL-backed CRUD).
 */
import React, { Component } from "react";
import StoreManager from './StoreManager';
import { API_URL } from '../../config';
import Pic from "../../media/Web_design_PNG.png";
import { Box, Typography } from "@mui/material";
import HelpHint from "../parts/helpHint";
import { CognitoAuthContext } from '../../aws-amplify-auth';

class Store extends Component {
  static contextType = CognitoAuthContext;
  state = { enabling: false };

  componentDidMount() {
    try {
      const params = new URLSearchParams(window.location.search);
      const ob = params.get('onboarding');
      if (ob === 'return' || ob === 'refresh') {
        // Refresh after returning from Stripe onboarding to pick up webhook-updated flags
        setTimeout(() => this.context?.refreshAuth?.(), 800);
      }
      if (!this.isEnabled()) {
        // Background refresh in case webhook lands slightly later
        setTimeout(() => this.context?.refreshAuth?.(), 4000);
      }
      this._focusHandler = () => {
        if (!this.isEnabled()) this.context?.refreshAuth?.();
        if (this.isEnabled() && this._focusHandler) window.removeEventListener('focus', this._focusHandler);
      };
      window.addEventListener('focus', this._focusHandler);
    } catch {}
  }
  componentWillUnmount() { if (this._focusHandler) window.removeEventListener('focus', this._focusHandler); }

  isEnabled() {
    const user = this.props.user || this.context?.user;
    return !!user?.app_metadata?.storeEnabled;
  }

  async enableStore() {
    try {
      this.setState({ enabling: true });
      const token = this.props.token || this.context?.token;
      const user = this.props.user || this.context?.user;
      const onboardingRes = await fetch(`${API_URL.replace('api.', 'api.')}/store/v1/connect/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      let onboardingUrl;
      if (onboardingRes.ok) { try { const j = await onboardingRes.json(); onboardingUrl = j.url; } catch {} }
      // Optimistic profile update to avoid UI delay (backend webhook is source of truth)
      await fetch(`${API_URL}/users/v1/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type: 'update', username: user?.user_metadata?.username || 'unknown', storeEnabled: true })
      });
      this.context?.refreshAuth?.();
      if (onboardingUrl) { window.location.href = onboardingUrl; return; }
      this.setState({ enabling: false });
    } catch (e) { console.error(e); this.setState({ enabling: false }); }
  }

  render() {
    const enabled = this.isEnabled();
    if (!enabled) {
      return (
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <img src={Pic} width={140} height={140} alt="Enable Store" />
          <Typography variant="h5" sx={{ mt: 2 }}>Start Selling on ShieldShot</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mt: 1 }}>
            Enable your store to list collections for sale. We'll walk you through Stripe Connect onboarding to receive payouts.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <button disabled={this.state.enabling} onClick={() => this.enableStore()} className="btn btn-primary">
              {this.state.enabling ? 'Enabling...' : 'Enable Store'}
            </button>
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={{ mt: 4, textAlign: 'left' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box />
          <HelpHint
            title="Store"
            hints={[
              "Browse your public collections",
              "Share a store link with your subscribers",
            ]}
          />
        </Box>
        <StoreManager token={this.props.token || this.context?.token} />
      </Box>
    );
  }
}

export default Store;
